import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../../assets/images/Logo-RollerUP.png";
import LanguageSwitcher from "../common/LanguageSwitcher";
import "./Header.css";

const Header = () => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const headerRef = useRef(null);
  const dropdownRef = useRef(null);
  const firstNavLinkRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const toggleBtnRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 968);
  const [scrolled, setScrolled] = useState(false);

  const openMobile = () => setMobileOpen(true);
  const closeMobile = () => setMobileOpen(false);
  const toggleMobileMenu = () => setMobileOpen((v) => !v);
  const toggleDropdown = () => setDropdownOpen((v) => !v);

  // Resize -> detectar móvil/escritorio y cerrar menú si pasa a desktop
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 968;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false);
      }
    };
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Click/touch fuera -> cierra dropdown y/o menú móvil
  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;

      // 1) Dropdown
      if (
        dropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setDropdownOpen(false);
      }

      // 2) Menú móvil (si está abierto y el click no fue en panel ni en el toggle)
      if (mobileOpen) {
        const clickedInsidePanel =
          mobileMenuRef.current && mobileMenuRef.current.contains(target);
        const clickedToggle =
          toggleBtnRef.current && toggleBtnRef.current.contains(target);

        if (!clickedInsidePanel && !clickedToggle) {
          setMobileOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, {
      passive: true,
    });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [mobileOpen, dropdownOpen]);

  // Sombra al scrollear + altura del header como var CSS
  useEffect(() => {
    const updateHeaderHeight = () => {
      const h = headerRef.current?.offsetHeight || 64;
      document.documentElement.style.setProperty("--header-h", `${h}px`);
    };
    
    const handleScroll = () => {
      if (!headerRef.current) return;
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
      
      if (isScrolled) {
        headerRef.current.classList.add("header--scrolled");
      } else {
        headerRef.current.classList.remove("header--scrolled");
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Body scroll lock + foco inicial al abrir
  useEffect(() => {
    const { body } = document;

    if (mobileOpen) {
      body.classList.add("menu-open");
      // foco al primer link del menú móvil
      setTimeout(() => firstNavLinkRef.current?.focus(), 120);
    } else {
      body.classList.remove("menu-open");
    }
    return () => body.classList.remove("menu-open");
  }, [mobileOpen]);

  // Cerrar con Escape
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (dropdownOpen) setDropdownOpen(false);
        if (mobileOpen) setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, dropdownOpen]);

  return (
    <header
      className={`header ${mobileOpen ? "header--overlay" : ""} ${
        scrolled ? "header--scrolled" : ""
      }`}
      ref={headerRef}
    >
      <div className="header__container">
        <Link
          to="/"
          className="header__logo"
          onClick={closeMobile}
          aria-label="Ir al inicio"
        >
          <img
            src={logo}
            alt="RollerUp"
            className="header__logo-img"
            loading="eager"
          />
        </Link>

        <button
          ref={toggleBtnRef}
          className={`header__mobile-toggle ${
            mobileOpen ? "header__mobile-toggle--open" : ""
          }`}
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          aria-expanded={mobileOpen}
          aria-controls="main-navigation"
          onClick={toggleMobileMenu}
          type="button"
        >
          <span className="header__toggle-line"></span>
          <span className="header__toggle-line"></span>
          <span className="header__toggle-line"></span>
        </button>

        {/* BACKDROP para móvil */}
        {isMobile && (
          <div
            className={`header__backdrop ${
              mobileOpen ? "header__backdrop--show" : ""
            }`}
            aria-hidden={!mobileOpen}
            onClick={closeMobile}
          />
        )}

        <nav
          id="main-navigation"
          className={`header__nav ${mobileOpen ? "header__nav--open" : ""}`}
          role="navigation"
          aria-label="Navegación principal"
          aria-hidden={isMobile ? !mobileOpen : false}
          ref={mobileMenuRef}
        >
          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? "header__link header__link--active" : "header__link"
            }
            onClick={closeMobile}
            end
            ref={firstNavLinkRef}
          >
            {t("nav.home")}
          </NavLink>

          <div className="header__dropdown" ref={dropdownRef}>
            <button
              className={`header__link header__dropdown-btn ${
                dropdownOpen ? "header__dropdown-btn--open" : ""
              }`}
              onClick={toggleDropdown}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
              type="button"
            >
              {t("nav.products")} 
              <span className="header__dropdown-arrow">
                <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
            </button>

            <div
              className={`header__dropdown-menu ${
                dropdownOpen ? "header__dropdown-menu--open" : ""
              }`}
              role="menu"
            >
              <Link
                to="/persianas"
                className="header__dropdown-link"
                role="menuitem"
                onClick={() => {
                  closeMobile();
                  setDropdownOpen(false);
                }}
              >
                {t("nav.products_sub.blinds")}
              </Link>
              <Link
                to="/toldos"
                className="header__dropdown-link"
                role="menuitem"
                onClick={() => {
                  closeMobile();
                  setDropdownOpen(false);
                }}
              >
                {t("nav.products_sub.awnings")}
              </Link>
              <Link
                to="/automatizacion"
                className="header__dropdown-link"
                role="menuitem"
                onClick={() => {
                  closeMobile();
                  setDropdownOpen(false);
                }}
              >
                {t("nav.products_sub.automation")}
              </Link>
            </div>
          </div>

          <NavLink
            to="/nosotros"
            className={({ isActive }) =>
              isActive ? "header__link header__link--active" : "header__link"
            }
            onClick={closeMobile}
          >
            {t("nav.aboutUs")}
          </NavLink>

          <NavLink
            to="/contacto"
            className={({ isActive }) =>
              isActive
                ? "header__btn-contact header__btn-contact--active"
                : "header__btn-contact"
            }
            onClick={closeMobile}
          >
            {t("nav.contact")}
          </NavLink>

          {/* En móvil: switcher dentro del menú */}
          {isMobile && (
            <div className="header__mobile-extra" aria-hidden={!mobileOpen}>
              <LanguageSwitcher />
            </div>
          )}
        </nav>

        {/* En desktop: switcher a la derecha */}
        {!isMobile && (
          <div className="header-right">
            <LanguageSwitcher />
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;